/**
 * Quick Actions chips for assistant messages with proposals or text-improve context.
 * "Aplicar" scrolls/highlights the proposal card — does NOT bypass ApprovalCenter.
 * "Regenerar" re-sends last user message (event); "Explicar" appends follow-up.
 * "Guardar borrador" copies content to localStorage draft keyed by entity.
 * "Enviar a aprobación" same as Aplicar (scrolls to ApprovalCenter proposal).
 */
import { useMemo, useState } from "react";
import { applyRenataFieldToForm } from "@/lib/renata/workspace-focus";
import { Check, Copy, GitCompare, X, RefreshCw, HelpCircle, Save, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DiffViewer } from "./DiffViewer";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { RenataProposal } from "@/lib/renata/types";

const TEXT_IMPROVE_INTENTS = new Set([
  "improve_text",
  "text-improve",
  "rewrite",
  "expand",
  "write_description",
  "create_field",
]);

interface RenataQuickActionsProps {
  proposals: RenataProposal[];
  content: string;
  contextIntent?: string | null;
  onCancelProposal?: (proposal: RenataProposal) => void;
  className?: string;
  /** Called when user clicks Regenerar — caller re-sends last user message. */
  onRegenerate?: () => void;
  /** Called when user clicks Explicar — caller appends follow-up question. */
  onExplain?: () => void;
  /** Entity key for localStorage draft (e.g. "producto:uuid"). */
  draftKey?: string;
}

function extractDiff(proposal: RenataProposal): { before: string; after: string } | null {
  const params = proposal.params ?? {};
  const before =
    typeof params.before === "string"
      ? params.before
      : params.before != null
        ? JSON.stringify(params.before, null, 2)
        : null;
  const after =
    typeof params.after === "string"
      ? params.after
      : params.after != null
        ? JSON.stringify(params.after, null, 2)
        : typeof params.preview === "string"
          ? params.preview
          : null;
  if (before == null && after == null) return null;
  return { before: before ?? "", after: after ?? "" };
}

function copyText(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast.success("Copiado al portapapeles"),
    () => toast.error("No se pudo copiar"),
  );
}

export function RenataQuickActions({
  proposals,
  content,
  contextIntent,
  onCancelProposal,
  className,
  onRegenerate,
  onExplain,
  draftKey,
}: RenataQuickActionsProps) {
  const [showDiff, setShowDiff] = useState(false);
  const [highlightProposalId, setHighlightProposalId] = useState<string | null>(null);

  const pendingProposal = proposals.find((p) => p.status === "pending") ?? proposals[0] ?? null;
  const diff = pendingProposal ? extractDiff(pendingProposal) : null;
  const isTextImprove = contextIntent ? TEXT_IMPROVE_INTENTS.has(contextIntent) : false;
  const isFieldApply =
    pendingProposal?.skill_id === "inline_field" || pendingProposal?.action === "apply_field";

  const copyContent = useMemo(() => {
    if (diff?.after) return diff.after;
    if (pendingProposal?.preview) return pendingProposal.preview;
    return content;
  }, [content, diff, pendingProposal]);

  if (!pendingProposal && !isTextImprove) return null;

  function handleApply() {
    if (!pendingProposal) return;
    if (pendingProposal.skill_id === "inline_field" || pendingProposal.action === "apply_field") {
      const field = String(pendingProposal.params?.field ?? "");
      const value = String(pendingProposal.params?.after ?? pendingProposal.preview ?? "");
      applyRenataFieldToForm({ field, value });
      return;
    }
    const el = document.getElementById(`renata-proposal-${pendingProposal.id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setHighlightProposalId(pendingProposal.id);
      window.setTimeout(() => setHighlightProposalId(null), 2000);
      const approveBtn = el.querySelector<HTMLButtonElement>('[data-renata-approve]');
      approveBtn?.focus();
    } else {
      toast.info("Revisá la propuesta debajo para aprobarla.");
    }
  }

  function handleCompare() {
    if (diff) {
      setShowDiff((v) => !v);
      return;
    }
    toast.info("No hay cambios para comparar.");
  }

  function handleCancel() {
    if (pendingProposal && onCancelProposal) {
      onCancelProposal(pendingProposal);
      return;
    }
    toast.message("Propuesta descartada. Podés regenerar o seguir editando.");
  }

  function handleRegenerate() {
    if (onRegenerate) {
      onRegenerate();
      return;
    }
    window.dispatchEvent(new CustomEvent("sm-renata-regenerate"));
    toast.info("Regenerando respuesta…");
  }

  function handleExplain() {
    if (onExplain) {
      onExplain();
      return;
    }
    window.dispatchEvent(new CustomEvent("sm-renata-explain", { detail: { content } }));
    toast.info("Solicitando explicación…");
  }

  function handleSaveDraft() {
    const key = draftKey ? `renata-draft:${draftKey}` : "renata-draft:last";
    const payload = diff?.after ?? copyContent;
    try {
      localStorage.setItem(key, JSON.stringify({ content: payload, savedAt: new Date().toISOString() }));
      toast.success("Borrador guardado localmente.");
    } catch {
      void navigator.clipboard.writeText(payload).then(
        () => toast.success("Copiado al portapapeles como borrador."),
        () => toast.error("No se pudo guardar el borrador."),
      );
    }
  }

  if (isFieldApply) {
    return (
      <div className={cn("w-full space-y-2", className)}>
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            className="h-7 gap-1 px-2.5 text-xs"
            onClick={handleApply}
          >
            <Check className="h-3 w-3" />
            Aplicar en el campo
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 px-2.5 text-xs"
            onClick={handleRegenerate}
          >
            <RefreshCw className="h-3 w-3" />
            Regenerar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 px-2.5 text-xs"
            onClick={() => copyText(copyContent)}
          >
            <Copy className="h-3 w-3" />
            Copiar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-2.5 text-xs text-muted-foreground"
            onClick={handleCancel}
          >
            <X className="h-3 w-3" />
            Descartar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-2", className)}>
      <div className="flex flex-wrap gap-1.5">
        {pendingProposal && (
          <Button
            size="sm"
            variant="secondary"
            className={cn(
              "h-7 gap-1 px-2.5 text-xs",
              highlightProposalId === pendingProposal.id && "ring-2 ring-violet-400",
            )}
            onClick={handleApply}
          >
            <Check className="h-3 w-3" />
            {isTextImprove ? "Aplicar en el campo" : "Aplicar"}
          </Button>
        )}
        {pendingProposal && (
          <Button
            size="sm"
            variant="secondary"
            className="h-7 gap-1 px-2.5 text-xs"
            onClick={handleApply}
            title="Enviar a aprobación"
          >
            <Send className="h-3 w-3" />
            Enviar a aprobación
          </Button>
        )}
        {(diff || isTextImprove) && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 px-2.5 text-xs"
            onClick={handleCompare}
          >
            <GitCompare className="h-3 w-3" />
            Comparar
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2.5 text-xs"
          onClick={handleRegenerate}
        >
          <RefreshCw className="h-3 w-3" />
          Regenerar
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2.5 text-xs"
          onClick={handleExplain}
        >
          <HelpCircle className="h-3 w-3" />
          Explicar
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2.5 text-xs"
          onClick={handleSaveDraft}
        >
          <Save className="h-3 w-3" />
          Guardar borrador
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2.5 text-xs"
          onClick={() => copyText(copyContent)}
        >
          <Copy className="h-3 w-3" />
          Copiar
        </Button>
        {pendingProposal && onCancelProposal && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-2.5 text-xs text-muted-foreground"
            onClick={handleCancel}
          >
            <X className="h-3 w-3" />
            Cancelar
          </Button>
        )}
      </div>

      {showDiff && diff && (
        <DiffViewer before={diff.before} after={diff.after} label="Comparación" />
      )}
    </div>
  );
}
